import { useState, useCallback } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import {
  useAllowedPaperWallets,
  useSavePaperWallet,
} from "../hooks/useAllowedPaperWallets";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  { header: "col.from", accessor: "lo", type: "number" },
  { header: "col.to", accessor: "hi", type: "number" },
];

const FORM_FIELDS = [
  { name: "lo", label: "form.from", type: "number" },
  { name: "hi", label: "form.to", type: "number" },
];

const CREATE_DEFAULTS = { lo: "", hi: "" };

export default function AllowedPaperWalletsPage() {
  const { t } = useTranslation();
  const { data: wallets, isLoading } = useAllowedPaperWallets();
  const saveMutation = useSavePaperWallet();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    setValues({ ...row });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    saveMutation.mutate(values, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordSaveFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.paperWallets")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable columns={COLUMNS} data={wallets} onRowClick={openEdit} />
      )}
      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addPaperWallet") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
